import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Telescope, Download } from "lucide-react";
import axios from "axios";
import BeforeAfter from "@/components/BeforeAfter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Share() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/${token}`)
      .then((r) => setDoc(r.data))
      .catch(() => setError("Lien introuvable ou révoqué."));
  }, [token]);

  if (error) return (
    <div className="min-h-screen grid place-items-center">
      <div className="text-center"><p className="text-muted-foreground">{error}</p></div>
    </div>
  );
  if (!doc) return <div className="min-h-screen grid place-items-center"><div className="orbital" /></div>;

  const r = doc.report || {};
  const before = `${API}/share/${token}/file/original`;
  const after = `${API}/share/${token}/file/processed`;
  return (
    <div className="min-h-screen relative">
      <div className="starfield" aria-hidden />
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="share-logo">
            <div className="w-9 h-9 rounded-md border border-white/10 grid place-items-center bg-gradient-to-br from-violet-500/30 to-blue-500/10">
              <Telescope className="w-4 h-4 text-violet-200" strokeWidth={1.5} />
            </div>
            <div className="font-medium tracking-tight">AstroLift</div>
          </a>
          <a href="/pricing" className="text-xs text-violet-300 hover:underline" data-testid="share-cta">Sublimez vos astrophotos →</a>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8" data-testid="share-page">
        <div>
          <div className="label-eyebrow">{r.mode_label} · {r.classification_label}</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-light tracking-tight">{doc.name}</h1>
          <div className="mt-2 flex flex-wrap gap-2">
            {(doc.tags || []).slice(0, 6).map((t) => (
              <span key={t} className="text-[11px] font-mono px-2 py-0.5 rounded border border-white/10 text-muted-foreground">{t}</span>
            ))}
          </div>
        </div>

        <BeforeAfter beforeSrc={before} afterSrc={after} />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric label="Score" value={`${r.global_score}/100`} />
          <Metric label="S/N" value={`${r.snr_before} → ${r.snr_after}`} />
          <Metric label="Détails" value={`${r.detail_gain_pct > 0 ? "+" : ""}${r.detail_gain_pct} %`} />
          <Metric label="Qualité ciel" value={`${r.sky_quality_after}/100`} />
        </div>

        <div className="rounded-md border border-white/10 bg-card p-6 text-center">
          <div className="label-eyebrow">Cette image a été traitée par AstroLift</div>
          <div className="mt-2 text-sm text-muted-foreground">Aucune génération artificielle. Uniquement l'extraction du signal réellement capturé.</div>
          <a href="/pricing" className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 glow-violet" data-testid="share-pricing-cta">
            <Download className="w-3.5 h-3.5" /> Essayer AstroLift gratuitement
          </a>
        </div>
      </main>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-md border border-white/10 bg-card p-4">
      <div className="label-eyebrow">{label}</div>
      <div className="font-mono text-lg mt-1">{value}</div>
    </div>
  );
}
